import api from './api';
import { ImageUtils } from '../utils/imageUtils';

export const livestreamService = {
  // Get livestreams (with query parameters)
  getLivestreams: async (params = {}) => {
    const response = await api.get('/api/livestreams/livestreams/', { params });
    return response.data;
  },

  // Get my livestreams (seller's livestreams)
  getMyLivestreams: async (params = {}) => {
    const response = await api.get('/api/livestreams/livestreams/my_livestreams/', { params });
    return response.data;
  },

  // Get live streams
  getLiveNow: async () => {
    const response = await api.get('/api/livestreams/livestreams/live_now/');
    return response.data;
  },

  // Get single livestream
  getLivestream: async (id) => {
    const response = await api.get(`/api/livestreams/livestreams/${id}/`);
    return response.data;
  },

  // Create livestream
  createLivestream: async (livestreamData) => {
    const response = await api.post('/api/livestreams/livestreams/', livestreamData);
    return response.data;
  },

  // Update livestream
  updateLivestream: async (id, livestreamData) => {
    const response = await api.put(`/api/livestreams/livestreams/${id}/`, livestreamData);
    return response.data;
  },

  // Delete livestream
  deleteLivestream: async (id) => {
    await api.delete(`/api/livestreams/livestreams/${id}/`);
  },

  // Start livestream
  startLivestream: async (id, data = {}) => {
    const response = await api.patch(`/api/livestreams/livestreams/${id}/start/`, data);
    return response.data;
  },

  // End livestream
  endLivestream: async (id, data = {}) => {
    const response = await api.patch(`/api/livestreams/livestreams/${id}/end/`, data);
    return response.data;
  },

  // Check and process credit deduction
  processCreditDeduction: async (id) => {
    const response = await api.post(`/api/livestreams/livestreams/${id}/check_credits/`);
    return response.data;
  },

  // Join livestream
  joinLivestream: async (id) => {
    const response = await api.post(`/api/livestreams/livestreams/${id}/join/`);
    return response.data;
  },

  // Leave livestream
  leaveLivestream: async (id) => {
    const response = await api.patch(`/api/livestreams/livestreams/${id}/leave/`);
    return response.data;
  },

  // Get Agora token
  getAgoraToken: async (id) => {
    const response = await api.get(`/api/livestreams/livestreams/${id}/agora-token/`);
    return response.data;
  },

  // Bidding Management
  // Get biddings (with query parameters)
  getBiddings: async (params = {}) => {
    const response = await api.get('/api/livestreams/biddings/', { params });
    return response.data;
  },

  // Get single bidding
  getBidding: async (id) => {
    const response = await api.get(`/api/livestreams/biddings/${id}/`);
    return response.data;
  },

  // Create product bidding (alias for easier use)
  startBidding: async (livestreamId, biddingData) => {
    const response = await api.post('/api/livestreams/biddings/', {
      livestream: livestreamId,
      ...biddingData
    });
    return response.data;
  },

  // Create product bidding
  createBidding: async (biddingData) => {
    const response = await api.post('/api/livestreams/biddings/', biddingData);
    return response.data;
  },

  // Update bidding
  updateBidding: async (id, biddingData) => {
    const response = await api.put(`/api/livestreams/biddings/${id}/`, biddingData);
    return response.data;
  },

  // End bidding
  endBidding: async (id) => {
    const response = await api.patch(`/api/livestreams/biddings/${id}/end/`);
    return response.data;
  },

  // Place bid (this is for buyers, but included for completeness)
  placeBid: async (id, bidData) => {
    const response = await api.post(`/api/livestreams/biddings/${id}/place-bid/`, bidData);
    return response.data;
  },

  // Send heartbeat to keep livestream alive and prevent auto-ending due to inactivity
  sendHeartbeat: async (id) => {
    const response = await api.post(`/api/livestreams/livestreams/${id}/heartbeat/`);
    return response.data;
  },

  // Duplicate livestream (create a copy with same data but reset status)
  duplicateLivestream: async (id) => {
    try {
      // First get the original livestream
      const originalLivestream = await livestreamService.getLivestream(id);
      
      // Create new FormData with the same data
      const formData = new FormData();
      formData.append('title', `Copy of ${originalLivestream.title}`.substring(0, 200));
      formData.append('description', originalLivestream.description);
      
      console.log('Duplicating livestream:', {
        id,
        originalTitle: originalLivestream.title,
        category: originalLivestream.category,
        subcategory: originalLivestream.subcategory,
        productsCount: originalLivestream.products?.length
      });

      // Handle category and subcategory (could be IDs or objects)
      const categoryId = typeof originalLivestream.category === 'object' ? originalLivestream.category.id : originalLivestream.category;
      
      if (!categoryId) {
        throw new Error('Original livestream is missing category information.');
      }
      
      formData.append('category', categoryId);
      
      if (originalLivestream.subcategory) {
        const subcategoryId = typeof originalLivestream.subcategory === 'object' ? originalLivestream.subcategory.id : originalLivestream.subcategory;
        formData.append('subcategory', subcategoryId);
      }
      
      // Add products if they exist
      if (originalLivestream.products && Array.isArray(originalLivestream.products)) {
        originalLivestream.products.forEach(product => {
          const productId = typeof product === 'object' ? product.id : product;
          if (productId) {
            formData.append('products', productId);
          }
        });
      }
      
      // Try to copy thumbnail if it exists
      if (originalLivestream.thumbnail) {
        try {
          const fullThumbnailUrl = ImageUtils.getFullImageUrl(originalLivestream.thumbnail);
          console.log('Fetching thumbnail from:', fullThumbnailUrl);
          
          // Fetch the image file from the thumbnail URL
          const imageResponse = await fetch(fullThumbnailUrl);
          if (imageResponse.ok) {
            const imageBlob = await imageResponse.blob();
            // Extract filename from URL or use a default
            const filename = originalLivestream.thumbnail.split('/').pop() || 'thumbnail.jpg';
            // Create a File object from the blob
            const imageFile = new File([imageBlob], `copy_${filename}`, { type: imageBlob.type });
            formData.append('thumbnail', imageFile);
          }
        } catch (thumbnailError) {
          console.warn('Could not copy thumbnail, proceeding without it:', thumbnailError);
          // Continue without thumbnail - user can upload a new one
        }
      }
      
      // Don't copy scheduled time or status - let user set these
      // Status will default to 'scheduled'
      
      const response = await api.post('/api/livestreams/livestreams/', formData);
      return response.data;
    } catch (error) {
      console.error('Error duplicating livestream:', error);
      throw error;
    }
  }
};




